import { useState, useEffect } from 'react';
import { personalLifeData } from '../../../mocks/personalLife';

export default function PersonalLife() {
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('personal');
    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  // Structure data for tab navigation
  const personalLife = [
    {
      category: 'Family & Relationships',
      title: personalLifeData.highlights[0].title,
      description: personalLifeData.highlights[0].description,
      images: [
        'https://static.readdy.ai/image/165557c1c60078d8d9753efdcb1d1569/1953bab5d81958b0cb039d0c64ffdc18.jpeg',
        'https://static.readdy.ai/image/165557c1c60078d8d9753efdcb1d1569/f153f7867326a04b96c6258e1d70274b.jpeg',
        'https://static.readdy.ai/image/165557c1c60078d8d9753efdcb1d1569/76bca7721af41ef5437f3c6899b0f7a4.jpeg'
      ],
      icon: 'ri-heart-line',
      highlights: [
        'Quality time with loved ones',
        'Creating lasting memories',
        'Travel adventures together',
        'Celebrating life\'s precious moments'
      ],
      quote: 'Family is not an important thing. It\'s everything.'
    },
    {
      category: 'Community Engagement',
      title: personalLifeData.highlights[1].title,
      description: personalLifeData.highlights[1].description,
      image: 'https://static.readdy.ai/image/165557c1c60078d8d9753efdcb1d1569/3f54e26e5759ea611793d1bcb78eac26.png',
      icon: 'ri-team-line',
      highlights: [
        'Mentorship programs',
        'Volunteer initiatives',
        'Charitable causes',
        'Supporting community growth'
      ],
      quote: 'We make a living by what we get, but we make a life by what we give.'
    },
    {
      category: 'Hobbies & Interests',
      title: 'Passions & Pursuits',
      description: 'A diverse range of interests that enrich my life, from creative pursuits to physical activities. These hobbies provide balance, inspiration, and opportunities for continuous personal growth and self-expression.',
      image: 'https://static.readdy.ai/image/165557c1c60078d8d9753efdcb1d1569/e673bb19131c142fd19143d25d6acdcf.jpeg',
      icon: 'ri-star-line',
      highlights: personalLifeData.interests.map(interest => `${interest.name}: ${interest.description}`),
      quote: 'Life is not about finding yourself. Life is about creating yourself.'
    }
  ];

  return (
    <section id="personal" className="relative py-28 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className={`mb-20 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="text-center mb-4">
            <span className="inline-block text-xs font-semibold tracking-[0.3em] uppercase text-[#800000] mb-6 px-8 py-2.5 border-2 border-[#800000]/20 bg-[#800000]/5" style={{ fontFamily: "'Inter', sans-serif" }}>Personal Life</span>
          </div>
          <h2 className="text-5xl md:text-7xl font-bold text-black text-center mb-6 tracking-tight" style={{ fontFamily: "'Inter', 'Helvetica Neue', sans-serif", letterSpacing: '-0.02em' }}>
            Beyond Achievements
          </h2>
          <p className="text-lg text-center text-gray-600 max-w-3xl mx-auto leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
            Exploring the passions, relationships, and personal pursuits that define who I am beyond professional accomplishments
          </p>
        </div>

        {/* Tab Navigation */}
        <div className={`mb-16 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="flex flex-wrap items-center justify-center gap-4">
            {personalLife.map((item, index) => (
              <button
                key={index}
                onClick={() => setActiveTab(index)}
                className={`group relative px-8 py-4 text-sm font-semibold uppercase tracking-wider transition-all duration-500 whitespace-nowrap cursor-pointer overflow-hidden ${
                  activeTab === index
                    ? 'bg-[#800000] text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-200 hover:border-[#800000]/30'
                }`}
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                <span className="relative z-10 flex items-center gap-2">
                  <i className={`${item.icon} text-base w-4 h-4 flex items-center justify-center`}></i>
                  {item.category}
                </span>
                {activeTab !== index && (
                  <div className="absolute inset-0 bg-[#800000] transform -translate-x-full group-hover:translate-x-0 transition-transform duration-500"></div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Content Area */}
        <div className="bg-white shadow-xl border border-gray-100 overflow-hidden">
          {personalLife.map((item, index) => (
            <div
              key={index}
              className={`transition-all duration-700 ${
                activeTab === index ? 'block animate-fadeIn' : 'hidden'
              }`}
            >
              <div className="grid lg:grid-cols-2 gap-0">
                {/* Image */}
                <div className="relative h-[500px] lg:h-auto overflow-hidden bg-gray-100">
                  {item.images ? (
                    /* Family section with multiple images */
                    <div className="w-full h-full grid grid-rows-3 gap-1">
                      {item.images.map((img, imgIdx) => (
                        <div key={imgIdx} className="relative overflow-hidden bg-gray-100 group">
                          <img
                            src={img}
                            alt={`${item.category} ${imgIdx + 1}`}
                            className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-r from-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* Single image for other sections */
                    <div className="relative w-full h-full group">
                      <img
                        src={item.image}
                        alt={item.category}
                        className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-12 lg:p-16 flex flex-col justify-center bg-gradient-to-br from-white to-gray-50/30">
                  <div className="mb-8">
                    <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-[#800000] to-[#600000] text-white mb-8 shadow-lg">
                      <i className={`${item.icon} text-3xl w-8 h-8 flex items-center justify-center`}></i>
                    </div>
                    <h3 className="text-4xl font-bold text-black mb-5 tracking-tight" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '-0.01em' }}>
                      {item.title}
                    </h3>
                    <p className="text-base text-gray-700 leading-relaxed mb-8" style={{ fontFamily: "'Inter', sans-serif", lineHeight: '1.9' }}>
                      {item.description}
                    </p>
                  </div>

                  {/* Highlights */}
                  <div className="space-y-4 mb-8">
                    {item.highlights.map((highlight, idx) => (
                      <div key={idx} className="flex items-start gap-4 group">
                        <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-[#800000]/10 text-[#800000] mt-0.5 transition-all duration-300 group-hover:bg-[#800000] group-hover:text-white">
                          <i className="ri-check-line text-sm w-4 h-4 flex items-center justify-center"></i>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed group-hover:text-black transition-colors duration-300 font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>
                          {highlight}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* Quote */}
                  {item.quote && (
                    <div className="mt-4 pt-8 border-t-2 border-[#800000]/10">
                      <blockquote className="relative pl-6 border-l-4 border-[#800000]">
                        <p className="text-lg font-light text-gray-800 italic leading-relaxed" style={{ fontFamily: "'Georgia', serif", lineHeight: '1.9' }}>
                          "{item.quote}"
                        </p>
                      </blockquote>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className={`mt-20 text-center transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <a
            href="#contact"
            className="group inline-flex items-center gap-3 bg-gradient-to-r from-[#800000] to-[#600000] text-white px-10 py-5 text-sm font-semibold uppercase tracking-wider hover:shadow-2xl hover:scale-105 transition-all duration-500 whitespace-nowrap cursor-pointer" style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <span>Let's Connect</span>
            <i className="ri-arrow-right-line text-lg group-hover:translate-x-2 transition-transform duration-300 w-5 h-5 flex items-center justify-center"></i>
          </a>
        </div>
      </div>
    </section>
  );
}
